import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { FEditableGridPayload, FEditableGridRow } from '../../../components/egrid';

import ActivityPlanningFilterBar from './ActivityPlanningFilterBar';
import ActivityTreePanel from './ActivityTreePanel';
//import TaskGridPanel from './TaskGridPanel';
//import BudgetLineGridPanel from './BudgetLineGridPanel';
import BudgetImpactPanel from './BudgetImpactPanel';
import TemplateLibraryPanel from './TemplateLibraryPanel';

import { ActivityNode, ActivityPlanningCards, ActivityTemplate } from './types';

import {
  addPlanningActivity,
  addPlanningTask,
  applyTemplate,
  fetchActivityPlan,
  fetchActivityTree,
  fetchBudgetLines,
  fetchPlanningCards,
  fetchTasks,
  fetchTemplates,
  saveBudgetLines,
  saveTasks,
  saveActivityPlan,
  fetchIndicatorPlan,
  saveIndicatorPlan,
} from './activityPlanningApi';

import {
  fetchGlobalObjectiveCombo,
  fetchSpecificObjectiveCombo,
  ComboRow,
} from './activityPlanningFilterApi';

// import ActivityTaskBudgetPanel, {
//   BudgetGridRow,
//   BudgetScope,
//   TaskGridRow,
// } from './ActivityTaskBudgetPanel';
import ActivityTaskBudgetPanel from './ActivityTaskBudgetPanel';

import {
  fetchTasksNormalized,
  fetchBudgetLinesNormalized,
} from './activityPlanningApi';
import ActivityPlanGridPanel from './ActivityPlanGridPanel';
import KpiPlanGridPanel from './KpiPlanGridPanel';
import { fetchCombo } from '../core/coreapi';
import ActivityFormPanel from './ActivityFormPanel';
import { FUnsavedChangesDialog } from '../grid';

type State = {
  // Filter values. These must remain shortcode/code values, not recids.
  cycleCode: string;
  costCenterCode: string;
  axisCode: string;
  globalObjectiveCode: string;
  specificObjectiveCode: string;
  statusCode: string;

  // Filter combos.
  accounts: ComboRow[];
  periods: ComboRow[];
  cycles: ComboRow[];
  costCenters: ComboRow[];
  axes: ComboRow[];
  globalObjectives: ComboRow[];
  specificObjectives: ComboRow[];

  // Workbench data.
  activities: ActivityNode[];
  selectedNode: ActivityNode | null;
  selectedTaskId: number | null;
  taskRows: FEditableGridRow[];
  budgetRows: FEditableGridRow[];
  cards: ActivityPlanningCards | null;
  templates: ActivityTemplate[];

  // Add Activity form.
  activityCode: string;
  activityName: string;
  activityCostCenterCode: string;
  activityWeight: number;
  activityDescription: string;

  // Add Task form.
  taskCode: string;
  taskName: string;
  taskWeight: number;
  taskDescription: string;

  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  //selectedTask: TaskGridRow | null;

  activityPlanRows: any[];
  activeTab: number;

  kpiPlanRows: any[];

  pendingAction: null | (() => Promise<void> | void);
  unsavedDialogOpen: boolean;

};

class ActivityPlanningWorkbench extends React.Component<any, State> {
  state: State = {
    cycleCode: '',
    costCenterCode: '',
    axisCode: '',
    globalObjectiveCode: '',
    specificObjectiveCode: '',
    statusCode: '1',

    accounts: [],
    periods: [],
    cycles: [],
    costCenters: [],
    axes: [],
    globalObjectives: [],
    specificObjectives: [],

    activities: [],
    selectedNode: null,
    selectedTaskId: null,
    taskRows: [],
    budgetRows: [],
    cards: null,
    templates: [],

    activityCode: '',
    activityName: '',
    activityCostCenterCode: '',
    activityWeight: 1,
    activityDescription: '',

    taskCode: '',
    taskName: '',
    taskWeight: 1,
    taskDescription: '',

    loading: false,
    saving: false,
    error: '',
    success: '',

    //selectedTask: null,

    activityPlanRows: [],
    activeTab: 0,

    kpiPlanRows: [],

    pendingAction: null as null | (() => Promise<void> | void),
    unsavedDialogOpen: false,

  };

  async componentDidMount() {
    await this.loadCombos();
    await this.load();
  }

  // ---------------------------------------------------------------------------
  // Generic combo loader
  // ---------------------------------------------------------------------------

  private postBiz = async (payload: any) => {
    const res = await fetch('/biz', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const json = await res.json();

    if (!res.ok || json.failure) {
      throw new Error(json.em || json.message || 'Request failed');
    }

    return json;
  };

  private jsonRows = (json: any): ComboRow[] => {
    const sd = json.sd || json.result?.sd || json.data?.sd || json;
    return Array.isArray(sd) ? sd : [];
  };

  private fetchCombo = async (service: string): Promise<ComboRow[]> => {
    const json = await this.postBiz({ s: service, a: 'combo' });
    return this.jsonRows(json);
  };

  loadCombos = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [cycles, costCenters, axes, periods, accounts] = await Promise.all([
        this.fetchCombo('budgetcycle'),
        this.fetchCombo('costcenter'),
        this.fetchCombo('axis'),
        fetchCombo('budgetperiod'),
        fetchCombo('chartofaccounts'),
      ]);

      this.setState({ cycles, costCenters, axes, periods, accounts });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  filters = () => {
    const {
      cycleCode,
      costCenterCode,
      axisCode,
      specificObjectiveCode,
      statusCode,
    } = this.state;

    return {
      cyc: cycleCode || null,
      ccc: costCenterCode || null,
      axc: axisCode || null,
      // Important: Planning Tree receives the Specific Objective shortcode.
      obc: specificObjectiveCode || null,
      stc: statusCode || '1',
    };
  };

  onCycleChange = (cycleCode: string) => {
    this.setState({ cycleCode, selectedNode: null }, this.load);
  };

  onCostCenterChange = (costCenterCode: string) => {
    this.setState({ costCenterCode, selectedNode: null }, this.load);
  };

  onAxisChange = async (axisCode: string) => {
    this.setState({
      axisCode,
      globalObjectiveCode: '',
      specificObjectiveCode: '',
      globalObjectives: [],
      specificObjectives: [],
      selectedNode: null,
      taskRows: [],
      budgetRows: [],
      cards: null,
      error: '',
      success: '',
    });

    if (!axisCode) {
      this.load();
      return;
    }

    try {
      const globalObjectives = await fetchGlobalObjectiveCombo(axisCode);
      this.setState({ globalObjectives });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    }
  };

  onGlobalObjectiveChange = async (globalObjectiveCode: string) => {
    const { axisCode } = this.state;

    this.setState({
      globalObjectiveCode,
      specificObjectiveCode: '',
      specificObjectives: [],
      selectedNode: null,
      taskRows: [],
      budgetRows: [],
      cards: null,
      error: '',
      success: '',
    });

    if (!axisCode || !globalObjectiveCode) return;

    try {
      const specificObjectives = await fetchSpecificObjectiveCombo(
        axisCode,
        globalObjectiveCode,
      );

      this.setState({ specificObjectives });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    }
  };

  onSpecificObjectiveChange = (specificObjectiveCode: string) => {
    this.setState(
      {
        specificObjectiveCode,
        selectedNode: null,
        taskRows: [],
        budgetRows: [],
        cards: null,
        error: '',
        success: '',
      },
      this.load,
    );
  };

  clearFilters = () => {
    this.setState(
      {
        cycleCode: '',
        costCenterCode: '',
        axisCode: '',
        globalObjectiveCode: '',
        specificObjectiveCode: '',
        globalObjectives: [],
        specificObjectives: [],
        selectedNode: null,
        selectedTaskId: null,
        taskRows: [],
        budgetRows: [],
        cards: null,
        error: '',
        success: '',
      },
      this.load,
    );
  };

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const [activities, templates] = await Promise.all([
        fetchActivityTree(this.filters()),
        fetchTemplates().catch(() => []),
      ]);

      this.setState({
        activities,
        templates,
        selectedNode: null,
        selectedTaskId: null,
        taskRows: [],
        budgetRows: [],
        cards: null,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  selectNode = async (node: ActivityNode) => {
    this.setState({
      selectedNode: node,
      selectedTaskId: node.nodetype === 'TASK' ? node.rid : null,
      loading: true,
      error: '',
      success: '',
      activityCostCenterCode: node.costcentercode || this.state.costCenterCode || '',
    });

    try {
      // For KPI in selectedNode
      if (node.nodetype === 'KPI') {
        const [kpiPlanRows, cards] = await Promise.all([
          fetchIndicatorPlan(node.kpiid || node.rid, this.state.costCenterCode).catch(() => []),
          fetchPlanningCards(node.kpiid || node.rid).catch(() => null),
        ]);

        this.setState({
          taskRows: [],
          budgetRows: [],
          activityPlanRows: [],
          kpiPlanRows,
          cards,
        });
        return;
      }
      // if (node.nodetype === 'ACTIVITY') {
      //   const activityId = node.activityid || node.rid;

      //   const [tasks, budgetRows, cards] = await Promise.all([
      //     fetchTasks(activityId).catch(() => []),
      //     fetchBudgetLines(null, activityId).catch(() => []),
      //     fetchPlanningCards(activityId).catch(() => null),
      //   ]);

      //   this.setState({ taskRows: tasks, budgetRows, cards });
      //   return;
      // }


      // For ACTIVITY in selectNode():
      if (node.nodetype === 'ACTIVITY') {
        const activityId = node.activityid || node.rid;

        const [tasks, budgetRows, cards, activityPlanRows] = await Promise.all([
          fetchTasksNormalized(activityId).catch(() => []),
          fetchBudgetLinesNormalized(null, activityId).catch(() => []),
          fetchPlanningCards(activityId).catch(() => null),
          fetchActivityPlan(activityId).catch(() => []),
        ]);

        // In selectNode after fetchTasks:
        const normalizedTasks = (tasks || []).map((r:any, idx:number) => ({
          ...r,
          id: r.id || r.rid || r.taskid || `task-${idx}`,
          _state: 'clean',
        }));

        //this.setState({ taskRows: tasks as any, budgetRows: budgetRows as any, cards, activityPlanRows, selectedTask: null });
        this.setState({ taskRows: normalizedTasks as any, budgetRows: budgetRows as any, cards, activityPlanRows, }); //selectedTask: null 
        return;
      }

      if (node.nodetype === 'TASK') {
        const [budgetRows, cards] = await Promise.all([
          fetchBudgetLines(node.rid, null).catch(() => []),
          fetchPlanningCards(node.rid).catch(() => null),
        ]);

        // After fetchBudgetLines:
        const normalizedBudgetRows = (budgetRows || []).map((r:any, idx:number) => ({
          ...r,
          id: r.id || r.rid || r.budgetlineid || `budget-${idx}`,
          _state: 'clean',
        }));

        // Then setState({ taskRows: normalizedTasks, budgetRows: normalizedBudgetRows, ... })
        //this.setState({ taskRows: [], budgetRows, cards });
        this.setState({ taskRows: [], budgetRows: normalizedBudgetRows, cards });
        return;
      }

      this.setState({ taskRows: [], budgetRows: [], cards: null });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  onTreeSelect = async (node: ActivityNode) => {
    await this.guardedRun(() => this.selectNode(node));
  };

  selectedActivityId = () => {
    const node = this.state.selectedNode;

    if (!node) return null;
    if (node.nodetype !== 'ACTIVITY') return null;

    return node.activityid || node.rid;
  };

  // ---------------------------------------------------------------------------
  // Add Activity / Add Task
  // ---------------------------------------------------------------------------

  setTextField = (name: keyof State) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const upperFields = ['activityCode', 'activityCostCenterCode', 'taskCode'];
    const numericFields = ['activityWeight', 'taskWeight'];

    let value: any = raw;

    if (upperFields.includes(String(name))) {
      value = String(raw || '').toUpperCase();
    }

    if (numericFields.includes(String(name))) {
      value = Number(raw || 0);
    }

    this.setState({ [name]: value } as any);
  };

  onFieldChange = (name:string, value: any) => {
    const upperFields = ['activityCode', 'activityCostCenterCode', 'taskCode'];
    const numericFields = ['activityWeight', 'taskWeight'];


    if (upperFields.includes(String(name))) {
      value = String(value || '').toUpperCase();
    }

    if (numericFields.includes(String(name))) {
      value = Number(value || 0);
    }

    this.setState({ [name]: value } as any);
  };

  addActivity = async () => {
    const {
      selectedNode,
      activityCode,
      activityName,
      activityCostCenterCode,
      activityWeight,
      activityDescription,
    } = this.state;

    if (!selectedNode || selectedNode.nodetype !== 'KPI') {
      this.setState({ error: 'Select a KPI node before adding an activity.' });
      return;
    }

    if (!activityCode || !activityName || !activityCostCenterCode) {
      this.setState({
        error: 'Activity code, activity name and cost center code are required.',
      });
      return;
    }

    this.setState({ saving: true, error: '', success: '' });

    try {
      await addPlanningActivity(
        selectedNode.kpiid || selectedNode.rid,
        activityName,
        activityCode,
        activityCostCenterCode,
        activityWeight,
        activityDescription,
      );

      this.setState({
        activityCode: '',
        activityName: '',
        activityDescription: '',
        activityWeight: 1,
        success: 'Activity created and linked to KPI.',
      });

      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ saving: false });
    }
  };

  addTask = async () => {
    const {
      selectedNode,
      taskCode,
      taskName,
      taskWeight,
      taskDescription,
    } = this.state;

    if (!selectedNode || selectedNode.nodetype !== 'ACTIVITY') {
      this.setState({ error: 'Select an activity node before adding a task.' });
      return;
    }

    if (!taskCode || !taskName) {
      this.setState({ error: 'Task code and task name are required.' });
      return;
    }

    this.setState({ saving: true, error: '', success: '' });

    try {
      await addPlanningTask(
        selectedNode.activityid || selectedNode.rid,
        taskName,
        taskCode,
        taskWeight,
        taskDescription,
      );

      this.setState({
        taskCode: '',
        taskName: '',
        taskDescription: '',
        taskWeight: 1,
        success: 'Task created under activity.',
      });

      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ saving: false });
    }
  };
  ////////
  submitTasksCollection = async (rows: any[]) => {
  const activityId = this.selectedActivityId();
  if (!activityId) throw new Error('Select an activity first.');

  const payloadRows = (rows || [])
    .filter((r: any) => r._state && r._state !== 'clean')
    .map((r: any) => ({
      rid: r.rid || r.taskid || null,
      taskcode: r.taskcode || '',
      taskname: r.taskname || '',
      weight: Number(r.weight || 0),
      taskdescription: r.taskdescription || '',
      status: r._state === 'deleted' ? 0 : 1,
      _state: r._state,
    }));

  await saveTasks(activityId, payloadRows);

  if (this.state.selectedNode) await this.selectNode(this.state.selectedNode);
};

submitBudgetLinesCollection = async (rows: any[]) => {
  const selectedTaskId = rows?.[0]?.taskid || rows?.[0]?.parentid || rows?.[0]?.pri || null;

  if (!selectedTaskId) throw new Error('Select a task first.');

  const payloadRows = (rows || [])
    .filter((r: any) => r._state && r._state !== 'clean')
    .map((r: any) => ({
      rid: r.rid || r.budgetlineid || null,
      taskid: selectedTaskId,
      accountcode: r.accountcode || '',
      description: r.description || '',
      amount: Number(r.amount || 0),
      status: r._state === 'deleted' ? 0 : 1,
      _state: r._state,
    }));

  await saveBudgetLines(0, selectedTaskId, payloadRows);

  if (this.state.selectedNode) await this.selectNode(this.state.selectedNode);
};

  // methods
  // selectTaskRow = (task: TaskGridRow | null) => {
  //   this.setState({ selectedTask: task } as any);
  // };

  // addTaskRow = () => {
  //   const tmpid = `new-task-${Date.now()}`;
  //   this.setState((prev) => ({
  //     taskRows: [
  //       ...prev.taskRows,
  //       {
  //         id: tmpid,
  //         code: '',
  //         name: '',
  //         description: '',
  //         weight: 1,
  //         status: '1',
  //         _state: 'new',
  //       } as any,
  //     ],
  //   }));
  // };

  // addRow = () => {
  //   const {activityCode} = this.state;
  //   const id = `new-task-${Date.now()}`;

  //   const newRow = {
  //     id,
  //     rid: null,
  //     code: activityCode?`${activityCode}.T`:'',
  //     name: '',
  //     weight: 0,
  //     description: '',
  //     _state: 'new',
  //   };

  //   this.props.onChange([...this.props.rows, newRow]);

  //   setTimeout(() => {
  //     this.setState({
  //       rowModesModel: {
  //         ...this.state.rowModesModel,
  //         [id]: { mode: GridRowModes.Edit },
  //       },
  //     });
  //   }, 0);
  // };

  // addBudgetLineRow = (scope: BudgetScope, task: TaskGridRow | null) => {
  //   const selectedActivity = this.state.selectedNode;
  //   if (!selectedActivity || selectedActivity.nodetype !== 'ACTIVITY') return;

  //   const activityId = selectedActivity.activityid || selectedActivity.rid;
  //   const tmpid = `new-budget-${Date.now()}`;

  //   this.setState((prev) => ({
  //     budgetRows: [
  //       ...prev.budgetRows,
  //       {
  //         id: tmpid,
  //         taskid: scope === 'TASK' ? Number(task?.rid || task?.id || 0) : null,
  //         activityid: activityId,
  //         accountcode: '',
  //         description: '',
  //         quantity: 1,
  //         unitprice: 0,
  //         amount: 0,
  //         remarks: '',
  //         _state: 'new',
  //       } as any,
  //     ],
  //   }));
  // };

  saveTaskGrid = async () => {
    const activityId = this.selectedActivityId();
    if (!activityId) throw new Error('Select an activity first.');

    //TODO: update from grid-delete-pattern.snippet.tsx
    const payloadRows = this.state.taskRows.map((r: any) => ({
      rid: r.rid || null,
      shc: r.code || '',
      nam: r.name || '',
      dsc: r.description || '',
      wgt: Number(r.weight || 1),
      sts: r.status || '1',
      _state: r._state || 'clean',
    }));

    await saveTasks(activityId, payloadRows);

    if (this.state.selectedNode) await this.selectNode(this.state.selectedNode);
  };

  saveBudgetGrid = async () => {
    const activityId = this.selectedActivityId();
    if (!activityId) throw new Error('Select an activity first.');

    //TODO: update from grid-delete-pattern.snippet.tsx
    const payloadRows = this.state.budgetRows.map((r: any) => ({
      rid: r.rid || null,
      tki: r.taskid || null,
      aci: r.activityid || activityId,
      acc: r.accountcode || '',
      dsc: r.description || '',
      qty: Number(r.quantity || 0),
      upr: Number(r.unitprice || 0),
      amt: Number(r.amount || 0),
      rmk: r.remarks || '',
      _state: r._state || 'clean',
    }));

    await saveBudgetLines(activityId, null, payloadRows);

    if (this.state.selectedNode) await this.selectNode(this.state.selectedNode);
  };

  // ---------------------------------------------------------------------------
  // Grids / Templates
  // ---------------------------------------------------------------------------
  submitKpiPlan = async (rows: any[]) => {
    const n = this.state.selectedNode;
    if (!n || n.nodetype !== 'KPI') throw new Error('Select a KPI first.');
    await saveIndicatorPlan(n.kpiid || n.rid, this.state.costCenterCode, rows);
    await this.selectNode(n);
  };

  //saveKpiPlanGrid = async (rows: any[]) => {
  submitKpiPlan1 = async (rows: any[]) => {
    const payloadRows = rows
      .filter((r: any) => r._state && r._state !== 'clean')
      .map((r: any) => ({
        rid: r.rid || r.recid || null,
        periodcode: r.periodcode,
        baselinevalue: Number(r.baselinevalue || 0),
        targetvalue: Number(r.targetvalue || 0),
        thresholdmin: Number(r.thresholdmin || 0),
        thresholdmax: Number(r.thresholdmax || 0),
        description: r.description || '',
        status: r._state === 'deleted' ? 0 : 1,
        _state: r._state,
      }));

    //await saveKpiPlan(kpiid, costCenterCode, payloadRows);
    const n = this.state.selectedNode;
    if (!n || n.nodetype !== 'KPI') throw new Error('Select a KPI first.');
    await saveIndicatorPlan(n.kpiid || n.rid, this.state.costCenterCode, payloadRows);
    await this.selectNode(n);
  };

  submitActivityPlan = async (rows: any[]) => {
    const activityId = this.selectedActivityId();
    if (!activityId) throw new Error('Select an activity first.');
    await saveActivityPlan(activityId, rows);
    if (this.state.selectedNode) {
      await this.selectNode(this.state.selectedNode);
    }
  };

  //saveKpiPlanGrid = async (rows: any[]) => {
  submitActivityPlan1 = async (rows: any[]) => {
    const payloadRows = rows
    .filter((r:any) => r._state && r._state !== 'clean')
    .map((r:any) => ({
      rid: r.rid || r.recid || null,
      periodcode: r.periodcode,
      drivervalue: Number(r.drivervalue || 0),
      targetvalue: Number(r.targetvalue || 0),
      plannedamount: Number(r.plannedamount || 0),
      description: r.description || '',
      status: r._state === 'deleted' ? 0 : 1,
      _state: r._state,
    }));

    //await saveActivityPlan(activityId, payloadRows);
    const activityId = this.selectedActivityId();
    if (!activityId) throw new Error('Select an activity first.');
    await saveActivityPlan(activityId, payloadRows);
    if (this.state.selectedNode) {
      await this.selectNode(this.state.selectedNode);
    }
  };

  

  // submitTasks = async (payload: FEditableGridPayload) => {
  //   const activityId = this.selectedActivityId();
  //   if (!activityId) throw new Error('Select an activity first.');
  //   await saveTasks(activityId, payload.rcd);
  //   if (this.state.selectedNode) {
  //     await this.selectNode(this.state.selectedNode);
  //   }
  // };

  // submitBudget = async (payload: FEditableGridPayload) => {
  //   const { selectedNode, selectedTaskId } = this.state;
  //   if (!selectedNode) throw new Error('Select an activity or task first.');
  //   const activityId =
  //     selectedNode.nodetype === 'ACTIVITY'
  //       ? selectedNode.activityid || selectedNode.rid
  //       : null;
  //   await saveBudgetLines(activityId || 0, selectedTaskId, payload.rcd);
  //   await this.selectNode(selectedNode);
  // };

  applyTemplateToActivity = async (templateCode: string) => {
    try{
      const activityId = this.selectedActivityId();
      if (!activityId) throw new Error('Select an activity first.');
      this.setState({ saving: true, error: '', success: '' });
      await applyTemplate(activityId, templateCode);
      if (this.state.selectedNode) {
        await this.selectNode(this.state.selectedNode);
      }
    }
    catch(err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ saving: false });
    }
  };

  //
  hasUnsavedChanges = () => {
    const rowsets = [
      this.state.taskRows,
      this.state.budgetRows,
      this.state.activityPlanRows,
      this.state.kpiPlanRows,
    ];

    return rowsets.some((rows: any[]) =>
      (rows || []).some((r: any) => r._state && r._state !== 'clean')
    );
  };

  guardedRun = async (action: () => Promise<void> | void) => {
    if (!this.hasUnsavedChanges()) {
      await action();
      return;
    }

    this.setState({
      unsavedDialogOpen: true,
      pendingAction: action,
    } as any);
  };

  saveAllDirty = async () => {
    const n = this.state.selectedNode;

    if (n?.nodetype === 'ACTIVITY') {
      if ((this.state.taskRows || []).some((r:any) => r._state && r._state !== 'clean')) {
        await this.saveTaskGrid();
      }
      if ((this.state.budgetRows || []).some((r:any) => r._state && r._state !== 'clean')) {
        await this.saveBudgetGrid();
      }
      if ((this.state.activityPlanRows || []).some((r:any) => r._state && r._state !== 'clean')) {
        await this.submitActivityPlan(this.state.activityPlanRows);
      }
    }

    if (n?.nodetype === 'KPI') {
      if ((this.state.kpiPlanRows || []).some((r:any) => r._state && r._state !== 'clean')) {
        await this.submitKpiPlan(this.state.kpiPlanRows);
      }
    }
  };

  discardAllDirty = async () => {
    const action = this.state.pendingAction;
    this.setState({
      unsavedDialogOpen: false,
      pendingAction: null,
    } as any);

    if (action) await action();
  };

  saveAndContinue = async () => {
    const action = this.state.pendingAction;

    await this.saveAllDirty();

    this.setState({
      unsavedDialogOpen: false,
      pendingAction: null,
    } as any);

    if (action) await action();
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  

  render() {
    const {
      loading,
      saving,
      error,
      success,
      activities,
      selectedNode,
      taskRows,
      budgetRows,
      activityPlanRows,
      kpiPlanRows,
      cards,
      templates,
      activeTab,
      periods,
      accounts,
    } = this.state;

    const { activityCode, activityName, costCenterCode, activityWeight, activityDescription } = this.state; //this.setTextField('activityCode')
    const { taskCode, taskName, taskWeight, taskDescription } = this.state;

    const selectedActivity = selectedNode?.nodetype === 'ACTIVITY' ? selectedNode : null;

    const isKpi = selectedNode?.nodetype === 'KPI';
    const isActivity = selectedNode?.nodetype === 'ACTIVITY';

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Activity Planning Workbench
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select KPI to create activities; select activity to create tasks. Header fields are filters only.
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={this.load}
            disabled={loading || saving}
          >
            Refresh
          </Button>
        </Stack>

        <ActivityPlanningFilterBar
          cycleCode={this.state.cycleCode}
          costCenterCode={this.state.costCenterCode}
          axisCode={this.state.axisCode}
          globalObjectiveCode={this.state.globalObjectiveCode}
          specificObjectiveCode={this.state.specificObjectiveCode}
          cycles={this.state.cycles}
          costCenters={this.state.costCenters}
          axes={this.state.axes}
          globalObjectives={this.state.globalObjectives}
          specificObjectives={this.state.specificObjectives}
          onCycleChange={this.onCycleChange}
          onCostCenterChange={this.onCostCenterChange}
          onAxisChange={this.onAxisChange}
          onGlobalObjectiveChange={this.onGlobalObjectiveChange}
          onSpecificObjectiveChange={this.onSpecificObjectiveChange}
          onRefresh={this.load}
          onClear={this.clearFilters}
          loading={loading || saving}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {(loading || saving) && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <CircularProgress size={22} />
            <Typography>
              {saving ? 'Saving...' : 'Loading activity planning data...'}
            </Typography>
          </Stack>
        )}

        <Stack spacing={3}>
          <BudgetImpactPanel cards={cards} />

          <Grid container spacing={3}>

            <Grid size={{ xs: 12, lg: 3 }}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <ActivityTreePanel
                  activities={activities}
                  selectedNid={selectedNode?.nid}
                  //onSelect={this.selectNode}
                  onSelect={this.onTreeSelect}
                />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              {/* {this.renderActionPanel()} */}
              <ActivityFormPanel
                selectedNode = {selectedNode}
                loading = {saving}
                saving = {saving}
                activityCode = {activityCode}
                activityName = {activityName}
                costCenter = {costCenterCode}
                activityWeight = {activityWeight}
                activityDescription = {activityDescription}
                taskCode = {taskCode}
                taskName = {taskName}
                taskWeight = {taskWeight}
                taskDescription = {taskDescription}
                onFieldChange = {this.onFieldChange}
                addActivity = {this.addActivity}
                addTask = {this.addTask}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper elevation={0} sx={{ p: 1 }}>
                <Tabs value={activeTab} onChange={(_, v) => this.setState({ activeTab: v })}>
                  {isActivity && <Tab label="Tasks & Budget Lines" />}
                  {isActivity && <Tab label="Activity Plan" />}
                  {isActivity && <Tab label="Templates" />}
                  {isKpi && <Tab label="KPI Plan" />}
                </Tabs>
                  {isActivity && activeTab === 0 && (
                    // <ActivityTaskBudgetPanel
                    //   accounts={accounts}
                    //   selectedNode={selectedNode}
                    //   taskRows={taskRows as any}
                    //   budgetRows={budgetRows as any}
                    //   loading={loading}
                    //   saving={saving}
                    //   onSelectTask={this.selectTaskRow}
                    //   onAddTask={this.addTaskRow}
                    //   onSaveTasks={this.saveTaskGrid}
                    //   onAddBudgetLine={this.addBudgetLineRow}
                    //   onSaveBudgetLines={this.saveBudgetGrid}
                    //   onTaskRowsChange={(rows) => this.setState({ taskRows: rows as any })}
                    //   onBudgetRowsChange={(rows) => this.setState({ budgetRows: rows as any })}
                    // />
                    <ActivityTaskBudgetPanel
                      taskRows={this.state.taskRows}
                      budgetRows={this.state.budgetRows}
                      accounts={this.state.accounts || []}
                      loading={this.state.loading}
                      onTaskRowsChange={(taskRows) => this.setState({ taskRows })}
                      onBudgetRowsChange={(budgetRows) => this.setState({ budgetRows })}
                      onSaveTasks={this.submitTasksCollection}
                      onSaveBudgetLines={this.submitBudgetLinesCollection}
                    />
                  )}

                  {isActivity && activeTab === 1 && (
                    <ActivityPlanGridPanel
                      periods={periods}
                      rows={activityPlanRows}
                      onChange={(activityPlanRows)=> this.setState({activityPlanRows})}
                      onSubmit={this.submitActivityPlan}
                    />
                  )}

                  {isActivity && activeTab === 2 && (
                    <TemplateLibraryPanel
                      templates={templates}
                      disabled={!selectedActivity}
                      applying={saving}
                      onApply={this.applyTemplateToActivity}
                    />
                  )}

                  {isKpi && activeTab === 0 && (
                    <KpiPlanGridPanel
                      periods={periods}
                      rows={kpiPlanRows}
                      onChange={(kpiPlanRows)=> this.setState({kpiPlanRows})}
                      onSubmit={this.submitKpiPlan}
                    />
                  )}
              </Paper>
            </Grid>

             

            {/* <Grid size={{ xs: 12, lg: 5 }}>
              <Stack spacing={2}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <TaskGridPanel
                    rows={taskRows}
                    onChange={(rows) => this.setState({ taskRows: rows })}
                    onSubmit={this.submitTasks}
                  />
                </Paper>

                <Paper elevation={1} sx={{ p: 2 }}>
                  <BudgetLineGridPanel
                    rows={budgetRows}
                    onChange={(rows) => this.setState({ budgetRows: rows })}
                    onSubmit={this.submitBudget}
                  />
                </Paper>
              </Stack>
            </Grid> */}
            {/* <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={2}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <ActivityTaskBudgetPanel
                    selectedNode={selectedNode}
                    taskRows={taskRows as any}
                    budgetRows={budgetRows as any}
                    loading={loading}
                    saving={saving}
                    onSelectTask={this.selectTaskRow}
                    onAddTask={this.addTaskRow}
                    onSaveTasks={this.saveTaskGrid}
                    onAddBudgetLine={this.addBudgetLineRow}
                    onSaveBudgetLines={this.saveBudgetGrid}
                    onTaskRowsChange={(rows) => this.setState({ taskRows: rows as any })}
                    onBudgetRowsChange={(rows) => this.setState({ budgetRows: rows as any })}
                  />
                </Paper>

                <Paper elevation={1} sx={{ p:2, mt:2 }}>
                  <ActivityPlanGridPanel
                      rows={activityPlanRows}
                      onChange={(activityPlanRows)=>
                          this.setState({activityPlanRows})
                      }
                      onSubmit={this.submitActivityPlan}
                  />
                </Paper>

                 <Paper elevation={1} sx={{ p:2, mt:2 }}>
                  <IndicatorPlanGridPanel
                      rows={kpiPlanRows}
                      onChange={(kpiPlanRows)=>
                          this.setState({kpiPlanRows})
                      }
                      onSubmit={this.submitKpiPlan}
                  />
                </Paper>
                
              </Stack>
            </Grid> */}

          </Grid>

          {/* <Grid container spacing={1}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <TemplateLibraryPanel
                  templates={templates}
                  disabled={!selectedActivity}
                  onApply={this.applyTemplateToActivity}
                />
              </Paper>
            </Grid>
          </Grid> */}
        </Stack>
        <FUnsavedChangesDialog
          open={this.state.unsavedDialogOpen}
          saving={this.state.saving}
          onSave={this.saveAndContinue}
          onDiscard={this.discardAllDirty}
          onCancel={() => this.setState({ unsavedDialogOpen: false, pendingAction: null } as any)}
        />
      </Box>
    );
  }
}

export default ActivityPlanningWorkbench;
